/**
 * Calls - Call Logs
 * View call history and details
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone,  PhoneIncoming, PhoneOutgoing, Clock, DollarSign, Search, Filter,
  ChevronLeft, ChevronRight, Eye, Download, Loader2, Trash2, AlertTriangle, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_STYLES = {
  completed: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
  incomplete: { icon: XCircle, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
  in_progress: { icon: Phone, color: 'text-blue-600', bg: 'bg-blue-100' },
  missed: { icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-100' }
};

const PROVIDER_STYLES = {
  openai: { color: 'bg-green-100 text-green-800' },
  deepgram: { color: 'bg-blue-100 text-blue-800' },
  custom: { color: 'bg-purple-100 text-purple-800' },
  'intent-ivr': { color: 'bg-amber-100 text-amber-800' }
};

export default function Calls() {
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === 'viewer';
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedCall, setSelectedCall] = useState(null);
  const [stats, setStats] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [limit, setLimit] = useState(10);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState([]);

  useEffect(() => {
    loadCalls();
    loadStats();
  }, [page, limit, statusFilter]);

  const loadCalls = async () => {
	  try {
		setLoading(true);
		const response = await api.getCalls({ page, limit, status: statusFilter || undefined });
		setCalls(response.data.calls || []);
		setTotal(response.data.pagination?.total || response.data.total || 0);
	  } catch (error) {
		console.error('Failed to load calls:', error);
	  } finally {
		setLoading(false);
	  }
	};

	const openCallModal = async (call) => {
		setSelectedCall(call);
		setSelectedTranscript([]);
		setTranscriptLoading(true);
		try {
			const res = await api.getCallTranscript(call.session_id);
			// API returns { transcript: [{speaker, text, timestamp}] }
			setSelectedTranscript(res.data.transcript || []);
		} catch (err) {
			// 404 means no transcript saved — not an error worth toasting
			setSelectedTranscript([]);
		} finally {
			setTranscriptLoading(false);
		}
	};

  const loadStats = async () => {
    try {
      const response = await api.getCallStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleDeleteAllCDRs = async () => {
	  try {
		setDeleting(true);
		await api.deleteAllCallLogs();
		toast.success('All call logs deleted successfully');
		setShowDeleteAllModal(false);
		setPage(1);
		loadCalls();
		loadStats();
	  } catch (error) {
		console.error('Failed to delete call logs:', error);
		toast.error('Failed to delete call logs');
	  } finally {
		setDeleting(false);
	  }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    return `$${(parseFloat(cost) || 0).toFixed(4)}`;
  };

  const filteredCalls = calls.filter(call => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      call.session_id?.toLowerCase().includes(searchLower) ||
      call.agent_name?.toLowerCase().includes(searchLower) ||
      call.caller_number?.includes(search)
    );
  });

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6">
      {/* Header */}
		<div className="flex items-center justify-between">
		  <div>
			<h1 className="text-2xl font-bold text-gray-900">Call Logs</h1>
			<p className="text-gray-500">View and analyze your voice call history</p>
		  </div>
		  {!isReadOnly && (
			<button
			  onClick={() => setShowDeleteAllModal(true)}
			  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
			>
			  <Trash2 className="w-4 h-4" />
			  Delete All CDRs
			</button>
		  )}
		</div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Phone className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Calls</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_calls || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completed_calls || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg Duration</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatDuration(Math.round(stats.avg_duration || 0))}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(parseFloat(stats.total_cost) || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by session ID, agent, or caller..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="in_progress">In Progress</option>
            <option value="missed">Missed</option>
          </select>
        </div>
      </div>

      {/* Call List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : filteredCalls.length === 0 ? (
          <div className="text-center py-12">
            <Phone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No calls found</h3>
            <p className="text-gray-500">
              {search || statusFilter ? 'Try adjusting your filters' : 'Calls will appear here once you start receiving them'}
            </p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Call
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCalls.map((call) => {
                  const statusStyle = STATUS_STYLES[call.status] || STATUS_STYLES.completed;
                  const StatusIcon = statusStyle.icon;
                  const providerStyle = PROVIDER_STYLES[call.provider] || PROVIDER_STYLES.openai;
                  
                  return (
                    <tr key={call.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${call.direction === 'inbound' ? 'bg-blue-100' : 'bg-green-100'}`}>
                            {call.direction === 'inbound' ? (
                              <PhoneIncoming className="w-4 h-4 text-blue-600" />
                            ) : (
                              <PhoneOutgoing className="w-4 h-4 text-green-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {call.caller_number || 'Unknown'}
                            </p>
                            <p className="text-xs text-gray-500 font-mono">
                              {call.session_id?.substring(0, 12)}...
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {call.agent_name || 'Unknown Agent'}
                          </p>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${providerStyle.color}`}>
                            {call.provider}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-sm text-gray-900">
                          <Clock className="w-4 h-4 text-gray-400" />
                          {formatDuration(call.duration_seconds)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {formatCost(call.total_cost)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {call.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(call.started_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => openCallModal(call)}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
			{total > 0 && (
			  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
				<div className="flex items-center gap-4">
				  <p className="text-sm text-gray-500">
					Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} calls
				  </p>
				  <select
					value={limit}
					onChange={(e) => {
					  setLimit(parseInt(e.target.value));
					  setPage(1);
					}}
					className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary-500"
				  >
					<option value={10}>10 per page</option>
					<option value={25}>25 per page</option>
					<option value={50}>50 per page</option>
					<option value={100}>100 per page</option>
				  </select>
				</div>
				<div className="flex items-center gap-2">
				  <button
					onClick={() => setPage(1)}
					disabled={page === 1}
					className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
				  >
					First
				  </button>
				  <button
					onClick={() => setPage(p => Math.max(1, p - 1))}
					disabled={page === 1}
					className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
				  >
					<ChevronLeft className="w-5 h-5" />
				  </button>
				  <span className="text-sm text-gray-700 px-2">
					Page {page} of {totalPages}
				  </span>
				  <button
					onClick={() => setPage(p => Math.min(totalPages, p + 1))}
					disabled={page === totalPages}
					className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
				  >
					<ChevronRight className="w-5 h-5" />
				  </button>
				  <button
					onClick={() => setPage(totalPages)}
					disabled={page === totalPages}
					className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
				  >
					Last
				  </button>
				</div>
			  </div>
			)}
          </>
        )}
      </div>

      {/* Call Detail Modal */}
      {selectedCall && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Call Details</h3>
              <button
                onClick={() => { setSelectedCall(null); setSelectedTranscript([]); }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500">Session ID</p>
                  <p className="font-mono text-sm">{selectedCall.session_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Agent</p>
                  <p className="font-medium">{selectedCall.agent_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="font-medium">{formatDuration(selectedCall.duration_seconds)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cost</p>
                  <p className="font-medium">{formatCost(selectedCall.total_cost)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Started</p>
                  <p className="font-medium">{new Date(selectedCall.started_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Caller</p>
                  <p className="font-medium">{selectedCall.caller_number || 'Unknown'}</p>
                </div>
              </div>

              {selectedCall.transcript && (
                <div>
					<h4 className="font-medium text-gray-900 mb-3">Transcript</h4>

					{transcriptLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="w-5 h-5 animate-spin text-primary-600 mr-2" />
							<span className="text-sm text-gray-500">Loading transcript...</span>
						</div>
					) : selectedTranscript.length === 0 ? (
						<div className="bg-gray-50 rounded-lg p-6 text-center">
							<p className="text-sm text-gray-400">No transcript available for this call</p>
						</div>
					) : (
						<div className="space-y-3 bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
							{selectedTranscript.map((msg, idx) => {
								const isUser = msg.speaker === 'user' || msg.role === 'user';
								return (
									<div key={idx} className={`flex gap-2 ${isUser ? 'justify-end' : ''}`}>
										<div className={`max-w-[80%] rounded-lg px-3 py-2 ${
											isUser ? 'bg-primary-100' : 'bg-white border border-gray-200'
										}`}>
											<p className="text-xs font-medium mb-1 ${isUser ? 'text-primary-600' : 'text-gray-500'}">
												{isUser ? 'Caller' : 'Agent'}
											</p>
											<p className="text-sm text-gray-800">
												{msg.text || msg.content}
											</p>
											{msg.timestamp && (
												<p className="text-xs text-gray-400 mt-1">
													{new Date(msg.timestamp).toLocaleTimeString()}
												</p>
											)}
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
              )}
            </div>
          </div>
        </div>
      )}
	  {/* Delete All Confirmation Modal */}
		{showDeleteAllModal && (
		  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
			<div className="bg-white rounded-lg shadow-xl max-w-md w-full">
			  <div className="p-6">
				<div className="flex items-center gap-4 mb-4">
				  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
					<AlertTriangle className="w-6 h-6 text-red-600" />
				  </div>
				  <div>
					<h3 className="text-lg font-semibold text-gray-900">Delete All Call Logs</h3>
					<p className="text-sm text-gray-500">This action cannot be undone</p>
				  </div>
				</div>
				
				<p className="text-gray-600 mb-6">
				  Are you sure you want to delete all {total} call log records? This will permanently remove all call history, transcripts, and analytics data.
				</p>
				
				<div className="flex items-center justify-end gap-3">
				  <button
					onClick={() => setShowDeleteAllModal(false)}
					disabled={deleting}
					className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
				  >
					Cancel
				  </button>
				  <button
					onClick={handleDeleteAllCDRs}
					disabled={deleting}
					className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
				  >
					{deleting ? (
					  <>
						<Loader2 className="w-4 h-4 animate-spin" />
						Deleting...
					  </>
					) : (
					  <>
						<Trash2 className="w-4 h-4" />
						Delete All
					  </>
					)}
				  </button>
				</div>
			  </div>
			</div>
		  </div>
		)}
    </div>
  );
}
